import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Play,
  MessageSquare,
  Send,
  Calendar,
  User,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  FileText,
  History,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Eye,
  Download,
  FolderOpen,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useState, useRef } from 'react';
import { SWISS_CANTONS } from '@bettermatch/shared';
import { api, Case, CaseExecution, PatientInfo, CaseDocument, getDocumentUrl, downloadDocument } from '../lib/api';
import { PostalCodeAutocomplete } from '../components/PostalCodeAutocomplete';
import { AuthenticatedImage } from '../components/AuthenticatedImage';
import { AuthenticatedPdfViewer } from '../components/AuthenticatedPdfViewer';
import { ConfirmDialog, PageHeader, Skeleton, CardSkeleton, Modal } from '../components/ui';
import { toast } from '../stores/toast-store';

export function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [executionToDelete, setExecutionToDelete] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    title: string;
    condition: string;
    patientInfo: PatientInfo;
  } | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<CaseDocument | null>(null);
  const [previewDocument, setPreviewDocument] = useState<CaseDocument | null>(null);
  const [editingComment, setEditingComment] = useState<{ id: string; content: string } | null>(null);
  const [commentToDelete, setCommentToDelete] = useState<{ id: string; content: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['case', id],
    queryFn: () => api.cases.get(id!),
    enabled: !!id,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch when component mounts
  });

  const addCommentMutation = useMutation({
    mutationFn: (content: string) => api.cases.addComment(id!, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      setNewComment('');
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      api.cases.updateComment(id!, commentId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      setEditingComment(null);
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => api.cases.deleteComment(id!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      setCommentToDelete(null);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED') => api.cases.update(id!, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
    },
  });

  const deleteExecutionMutation = useMutation({
    mutationFn: (executionId: string) => api.executions.delete(id!, executionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      setExecutionToDelete(null);
      toast.success('Execution deleted', 'The execution has been permanently deleted.');
    },
    onError: (error) => {
      toast.error('Failed to delete', error instanceof Error ? error.message : 'An error occurred');
    },
  });

  const updateCaseMutation = useMutation({
    mutationFn: (data: { title?: string; condition?: string; patientInfo?: PatientInfo }) =>
      api.cases.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      setIsEditing(false);
      setEditForm(null);
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) => api.cases.deleteDocument(id!, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      setDocumentToDelete(null);
      toast.success('Document deleted', 'The document has been removed.');
    },
    onError: (error) => {
      toast.error('Failed to delete', error instanceof Error ? error.message : 'An error occurred');
    },
  });

  const uploadDocumentsMutation = useMutation({
    mutationFn: (files: File[]) => api.cases.uploadDocuments(id!, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-4">
          <Skeleton width={200} height={20} className="mb-2" />
          <Skeleton width={300} height={16} />
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <CardSkeleton />
            <CardSkeleton />
          </div>
          <div className="space-y-4">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
        <p className="mt-2 text-lg font-medium text-gray-900">Case not found</p>
        <Link to="/cases" className="btn-primary mt-4">
          Back to Cases
        </Link>
      </div>
    );
  }

  const caseData = data.data as Case & {
    comments?: Array<{
      id: string;
      content: string;
      createdAt: string;
      author: { id: string; name: string };
    }>;
  };

  // Get all executions sorted by date (newest first)
  const allExecutions = (caseData.executions || []).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const latestExecution = allExecutions[0] as CaseExecution | undefined;

  // Filter completed executions for "Previous Runs" (exclude latest, only show COMPLETED)
  const previousCompletedExecutions = allExecutions
    .slice(1)
    .filter((exec) => exec.status === 'COMPLETED');

  // Step labels for display
  const stepLabels: Record<string, string> = {
    questionnaire: 'Patient Questionnaire',
    answers: 'Answers',
    indication: 'Indication',
    criteria: 'Matching Criteria',
    recommendations: 'Recommendations',
  };

  const allSteps = ['questionnaire', 'answers', 'indication', 'criteria', 'recommendations'];

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-blue-100 text-blue-800',
    ACTIVE: 'bg-yellow-100 text-yellow-800',
    ARCHIVED: 'bg-gray-100 text-gray-600',
  };

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      addCommentMutation.mutate(newComment);
    }
  };

  const handleStartEdit = () => {
    setEditForm({
      title: caseData.title,
      condition: caseData.condition,
      patientInfo: caseData.patientInfo || {},
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm(null);
  };

  const handleSaveEdit = () => {
    if (editForm) {
      updateCaseMutation.mutate(editForm);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadDocumentsMutation.mutate(Array.from(files));
    }
  };

  const getDocumentIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const canEdit = allExecutions.length === 0;

  const statusLabels: Record<string, string> = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    ARCHIVED: 'Archived',
  };

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader
        breadcrumbs={[
          { label: 'Cases', href: '/cases', icon: FolderOpen },
          { label: caseData.title },
        ]}
        title={caseData.title}
        description={
          <span className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" aria-hidden="true" />
              {caseData.createdBy.name}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              {formatDistanceToNow(new Date(caseData.createdAt), { addSuffix: true })}
            </span>
            <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${statusColors[caseData.status]}`}>
              {statusLabels[caseData.status]}
            </span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {canEdit && !isEditing && (
              <button onClick={handleStartEdit} className="btn-outline">
                <Pencil className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="btn-outline"
                  disabled={updateCaseMutation.isPending}
                >
                  <X className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn-primary"
                  disabled={updateCaseMutation.isPending}
                >
                  {updateCaseMutation.isPending ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  )}
                  Save
                </button>
              </>
            )}
            {!isEditing && (
              <>
                <select
                  value={caseData.status}
                  onChange={(e) => updateStatusMutation.mutate(e.target.value as 'DRAFT' | 'ACTIVE' | 'ARCHIVED')}
                  className="input text-sm w-28"
                  aria-label="Change case status"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
                {caseData.status === 'ACTIVE' ? (
                  <Link to={`/cases/${id}/andy-pro?autostart=true`} className="btn-primary">
                    <Play className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    Analyse Case
                  </Link>
                ) : (
                  <button
                    onClick={() => updateStatusMutation.mutate('ACTIVE')}
                    className="btn-primary"
                    disabled={updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <CheckCircle className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    )}
                    Activate Case
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Title (edit mode) */}
          {isEditing && editForm && (
            <div className="card">
              <h2 className="text-sm font-medium text-gray-700 mb-2">Title</h2>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="input w-full"
              />
            </div>
          )}

          {/* Condition */}
          <div className="card">
            <h2 className="text-sm font-medium text-gray-700 mb-2">Medical Condition</h2>
            {isEditing && editForm ? (
              <textarea
                value={editForm.condition}
                onChange={(e) => setEditForm({ ...editForm, condition: e.target.value })}
                className="input w-full min-h-[100px]"
                rows={4}
              />
            ) : (
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{caseData.condition}</p>
            )}
          </div>

          {/* Patient Info - Inline */}
          {(isEditing || caseData.patientInfo?.age || caseData.patientInfo?.gender || caseData.patientInfo?.canton) && (
            <div className="card">
              <h2 className="text-sm font-medium text-gray-700 mb-2">Patient Information</h2>
              {isEditing && editForm ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Age</label>
                    <input
                      type="number"
                      value={editForm.patientInfo?.age || ''}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          patientInfo: {
                            ...editForm.patientInfo,
                            age: e.target.value ? parseInt(e.target.value) : undefined,
                          },
                        })
                      }
                      className="input w-full"
                      placeholder="Age"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Gender</label>
                    <select
                      value={editForm.patientInfo?.gender || ''}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          patientInfo: {
                            ...editForm.patientInfo,
                            gender: (e.target.value as 'male' | 'female' | 'other') || undefined,
                          },
                        })
                      }
                      className="input w-full"
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <PostalCodeAutocomplete
                      value={editForm.patientInfo?.postalCode || ''}
                      onChange={(plz, canton) =>
                        setEditForm({
                          ...editForm,
                          patientInfo: {
                            ...editForm.patientInfo,
                            postalCode: plz || undefined,
                            canton: canton || undefined,
                          },
                        })
                      }
                      label="Postal Code"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Canton</label>
                    <input
                      type="text"
                      value={
                        editForm.patientInfo?.canton
                          ? SWISS_CANTONS.find((c) => c.code === editForm.patientInfo?.canton)?.name ||
                            editForm.patientInfo.canton
                          : ''
                      }
                      readOnly
                      className="input w-full bg-gray-50 text-gray-500"
                      placeholder="Auto-filled"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-4 text-sm">
                  {caseData.patientInfo?.age && (
                    <div>
                      <span className="text-gray-500">Age:</span>{' '}
                      <span className="text-gray-900">{caseData.patientInfo.age}</span>
                    </div>
                  )}
                  {caseData.patientInfo?.gender && (
                    <div>
                      <span className="text-gray-500">Gender:</span>{' '}
                      <span className="text-gray-900 capitalize">{caseData.patientInfo.gender}</span>
                    </div>
                  )}
                  {caseData.patientInfo?.canton && (
                    <div>
                      <span className="text-gray-500">Canton:</span>{' '}
                      <span className="text-gray-900">{caseData.patientInfo.canton}</span>
                    </div>
                  )}
                  {caseData.patientInfo?.postalCode && (
                    <div>
                      <span className="text-gray-500">PLZ:</span>{' '}
                      <span className="text-gray-900">{caseData.patientInfo.postalCode}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Documents */}
          {(caseData.documents?.length > 0 || canEdit) && (
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-medium text-gray-700">
                  Documents {caseData.documents?.length > 0 && `(${caseData.documents.length})`}
                </h2>
                {canEdit && (
                  <label className="btn-outline text-xs px-2 py-1 cursor-pointer">
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    {uploadDocumentsMutation.isPending ? 'Uploading...' : 'Upload'}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                    />
                  </label>
                )}
              </div>
              {caseData.documents && caseData.documents.length > 0 ? (
                <div className="space-y-1">
                  {caseData.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-2 rounded bg-gray-50 px-2 py-1.5 group"
                    >
                      <span className="text-base">{getDocumentIcon(doc.mimeType)}</span>
                      <span className="text-sm text-gray-900 truncate flex-1">{doc.originalName}</span>
                      <span className="text-xs text-gray-400">{(doc.size / 1024).toFixed(1)} KB</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setPreviewDocument(doc)}
                          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Preview"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => downloadDocument(doc.url, doc.originalName)}
                          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Download"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => setDocumentToDelete(doc)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No documents uploaded yet
                </p>
              )}
            </div>
          )}

          {/* Execution History */}
          {allExecutions.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <h2 className="text-sm font-medium text-gray-700">
                    Analysis Runs ({allExecutions.length})
                  </h2>
                </div>
              </div>

              {/* Latest Execution - Featured */}
              {latestExecution && (() => {
                const completedSteps = (latestExecution.completedSteps as string[]) || [];
                const isInProgress = latestExecution.status === 'IN_PROGRESS';
                const isFailed = latestExecution.status === 'FAILED';
                const stepsCount = completedSteps.length;
                const totalSteps = allSteps.length;

                return (
                  <div
                    className={`rounded-lg border-2 px-4 py-3 mb-3 ${
                      isInProgress
                        ? 'border-yellow-300 bg-yellow-50'
                        : isFailed
                        ? 'border-red-300 bg-red-50'
                        : 'border-primary-200 bg-primary-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">Latest Run</span>
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            isInProgress
                              ? 'bg-yellow-200 text-yellow-800'
                              : isFailed
                              ? 'bg-red-200 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {isInProgress ? 'In Progress' : isFailed ? 'Failed' : 'Completed'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(latestExecution.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-2 bg-white rounded-full overflow-hidden border border-gray-200">
                        <div
                          className={`h-full rounded-full transition-all ${
                            isInProgress
                              ? 'bg-yellow-500'
                              : isFailed
                              ? 'bg-red-500'
                              : 'bg-green-500'
                          }`}
                          style={{ width: `${(stepsCount / totalSteps) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 font-medium whitespace-nowrap">
                        {stepsCount}/{totalSteps} steps
                      </span>
                    </div>
                    {/* Completed steps */}
                    {completedSteps.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {completedSteps.map((step) => (
                          <span
                            key={step}
                            className="inline-flex items-center gap-0.5 rounded bg-white px-1.5 py-0.5 text-xs text-gray-700 border border-gray-200"
                          >
                            <CheckCircle className="h-3 w-3 text-green-500" aria-hidden="true" />
                            {stepLabels[step] || step}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {isInProgress ? (
                        <Link
                          to={`/cases/${id}/andy-pro?executionId=${latestExecution.id}`}
                          className="btn-primary text-sm"
                        >
                          Resume Analysis
                        </Link>
                      ) : !isFailed && stepsCount > 0 ? (
                        <Link
                          to={`/cases/${id}/andy-pro?executionId=${latestExecution.id}`}
                          className="btn-primary text-sm"
                        >
                          View Results
                        </Link>
                      ) : null}
                      <button
                        onClick={() => setExecutionToDelete(latestExecution.id)}
                        className="btn-outline text-sm text-gray-500"
                        aria-label="Delete this execution"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Previous Executions - Collapsed (only COMPLETED runs) */}
              {previousCompletedExecutions.length > 0 && (
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors list-none">
                    <span className="text-xs font-medium text-gray-600">
                      Previous Runs ({previousCompletedExecutions.length})
                    </span>
                    <svg
                      className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-180"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="mt-2 space-y-2">
                    {previousCompletedExecutions.map((execution, index) => {
                      const completedSteps = (execution.completedSteps as string[]) || [];
                      const stepsCount = completedSteps.length;
                      const totalSteps = allSteps.length;
                      const runNumber = previousCompletedExecutions.length - index;

                      return (
                        <div
                          key={execution.id}
                          className="rounded border px-3 py-2 border-gray-200 bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle className="h-4 w-4" aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-700">Run #{runNumber}</span>
                                  <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700">
                                    Completed
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(execution.createdAt), { addSuffix: true })}
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {stepsCount}/{totalSteps} steps completed
                              </div>
                            </div>
                            {/* Action buttons */}
                            <div className="flex items-center gap-1">
                              <Link
                                to={`/cases/${id}/andy-pro?executionId=${execution.id}`}
                                className="btn-outline text-xs px-2 py-1"
                              >
                                View
                              </Link>
                              <button
                                onClick={() => setExecutionToDelete(execution.id)}
                                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                aria-label={`Delete run #${runNumber}`}
                              >
                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Analysis Status - based on completedSteps */}
          <div className="card">
            <h2 className="text-sm font-medium text-gray-700 mb-3">
              {latestExecution ? 'Analysis Progress' : 'Not Started'}
            </h2>
            {(() => {
              const completedSteps = (latestExecution?.completedSteps as string[]) || [];
              const progressSteps = [
                { id: 'questionnaire', label: 'Patient Questionnaire' },
                { id: 'answers', label: 'Answers' },
                { id: 'indication', label: 'Indication' },
                { id: 'criteria', label: 'Matching Criteria' },
                { id: 'recommendations', label: 'Recommendations' },
              ];
              return (
                <div className="space-y-2">
                  {progressSteps.map((step) => {
                    const isComplete = completedSteps.includes(step.id);
                    return (
                      <div key={step.id} className="flex items-center gap-2">
                        {isComplete ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Clock className="h-4 w-4 text-gray-300" />
                        )}
                        <span className={`text-sm ${isComplete ? 'text-gray-900' : 'text-gray-400'}`}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Comments */}
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="h-4 w-4 text-gray-400" />
              <h2 className="text-sm font-medium text-gray-700">
                Comments ({caseData.comments?.length || 0})
              </h2>
            </div>

            {caseData.comments && caseData.comments.length > 0 && (
              <div className="space-y-3 mb-3 max-h-48 overflow-y-auto">
                {caseData.comments.map((comment) => (
                  <div key={comment.id} className="border-l-2 border-gray-200 pl-2 group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span className="font-medium text-gray-700">{comment.author.name}</span>
                        <span>&middot;</span>
                        <span>{format(new Date(comment.createdAt), 'MMM d, HH:mm')}</span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditingComment({ id: comment.id, content: comment.content })}
                          className="p-0.5 text-gray-400 hover:text-primary-600"
                          title="Edit"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => setCommentToDelete({ id: comment.id, content: comment.content })}
                          className="p-0.5 text-gray-400 hover:text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    {editingComment?.id === comment.id ? (
                      <div className="mt-1 flex gap-1">
                        <input
                          type="text"
                          value={editingComment.content}
                          onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })}
                          className="input flex-1 text-sm py-1"
                          autoFocus
                        />
                        <button
                          onClick={() => updateCommentMutation.mutate({
                            commentId: editingComment.id,
                            content: editingComment.content,
                          })}
                          disabled={!editingComment.content.trim() || updateCommentMutation.isPending}
                          className="btn-primary px-2 py-1"
                        >
                          {updateCommentMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Save className="h-3 w-3" />
                          )}
                        </button>
                        <button
                          onClick={() => setEditingComment(null)}
                          className="btn-outline px-2 py-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700">{comment.content}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleSubmitComment} className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add comment..."
                className="input flex-1 text-sm"
              />
              <button
                type="submit"
                disabled={!newComment.trim() || addCommentMutation.isPending}
                className="btn-primary px-2.5"
              >
                {addCommentMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Execution Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!executionToDelete}
        onClose={() => setExecutionToDelete(null)}
        onConfirm={() => executionToDelete && deleteExecutionMutation.mutate(executionToDelete)}
        title="Delete Execution"
        message="Are you sure you want to delete this execution? All patient questionnaire responses, analysis results, and recommendations will be permanently removed."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteExecutionMutation.isPending}
      />

      {/* Delete Document Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={() => documentToDelete && deleteDocumentMutation.mutate(documentToDelete.id)}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.originalName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteDocumentMutation.isPending}
      />

      {/* Delete Comment Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!commentToDelete}
        onClose={() => setCommentToDelete(null)}
        onConfirm={() => commentToDelete && deleteCommentMutation.mutate(commentToDelete.id)}
        title="Delete Comment"
        message={`Are you sure you want to delete this comment? "${commentToDelete?.content?.slice(0, 50)}${(commentToDelete?.content?.length ?? 0) > 50 ? '...' : ''}"`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteCommentMutation.isPending}
      />

      {/* Document Preview Dialog */}
      {previewDocument && (
        <Modal
          isOpen={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          title={previewDocument.originalName}
          size="lg"
        >
          <div className="flex justify-end mb-4">
            <button
              onClick={() => downloadDocument(previewDocument.url, previewDocument.originalName)}
              className="btn-outline text-sm"
            >
              <Download className="h-4 w-4 mr-1" aria-hidden="true" />
              Download
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 min-h-[300px]">
            {previewDocument.mimeType.startsWith('image/') ? (
              <AuthenticatedImage
                src={getDocumentUrl(previewDocument.url)}
                alt={previewDocument.originalName}
                className="max-w-full h-auto mx-auto"
              />
            ) : previewDocument.mimeType === 'application/pdf' ? (
              <AuthenticatedPdfViewer
                src={getDocumentUrl(previewDocument.url)}
                title={previewDocument.originalName}
                className="w-full h-[60vh]"
              />
            ) : (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" aria-hidden="true" />
                <p className="text-gray-500 mb-4">
                  Preview not available for this file type
                </p>
                <button
                  onClick={() => downloadDocument(previewDocument.url, previewDocument.originalName)}
                  className="btn-primary"
                >
                  <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                  Download File
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
